import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, type ApiError } from '../../api/client';
import { Alert } from '../../components/Field';
import { ContentPreviewModal } from '../../components/ContentPreviewModal';
import { duration, money, titleCase } from '../../lib/format';

/** A course that has been submitted and is waiting on a decision. */
interface CourseRow {
  id: string;
  title: string;
  category: string;
  moduleCount: number;
  durationMinutes: number;
  priceMinor: number;
  currency: string;
  isFree: boolean;
  instructors: string[];
  updatedAt: string;
}

interface ContentRow {
  id: string;
  title: string;
  kind: string;
  status: string;
  versions: number;
  servable: boolean;
  scanStatus: string | null;
}

interface ReviewRow {
  id: string;
  course: string;
  rating: number;
  title: string | null;
  body: string | null;
  createdAt: string;
}

/**
 * The approval gate. Courses waiting to go live, content waiting for review
 * and learner reviews waiting for moderation share a screen because they are
 * the same job: deciding what the public sees.
 *
 * Courses were missing from it. A course submitted for review moved to the
 * REVIEW state and then appeared nowhere — not here, and nowhere else either.
 * The only way to find one was to know to open Courses and set the status
 * filter by hand, which is not a queue, and is unreachable for a REVIEWER,
 * whose whole job this screen is.
 */
/**
 * Courses fetched per page — the API's maximum.
 *
 * The queue used to take the API's default of 25 and count only those, so
 * the 26th submission waited unseen, and the heading said 25.
 */
const COURSE_PAGE_SIZE = 100;

const coursesPath = (page: number) =>
  `/admin/courses?status=REVIEW&page=${page}&pageSize=${COURSE_PAGE_SIZE}`;

interface CoursePage {
  items: CourseRow[];
  page: number;
  totalItems: number;
  totalPages: number;
}

export function ReviewQueue() {
  const [courses, setCourses] = useState<CourseRow[] | null>(null);
  /** Every course awaiting review, including any past the pages loaded. */
  const [courseTotal, setCourseTotal] = useState(0);
  const [coursePage, setCoursePage] = useState(1);
  const [content, setContent] = useState<ContentRow[] | null>(null);
  const [reviews, setReviews] = useState<ReviewRow[] | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [note, setNote] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState<ContentRow | null>(null);

  const load = useCallback(() => {
    api<CoursePage>(coursesPath(1))
      .then((result) => {
        setCourses(result.items);
        setCourseTotal(result.totalItems);
        setCoursePage(1);
      })
      .catch((caught) => setError(caught as ApiError));
    api<{ items: ContentRow[] }>('/content?status=REVIEW')
      .then((result) => setContent(result.items))
      .catch((caught) => setError(caught as ApiError));
    api<{ items: ReviewRow[] }>('/admin/reviews')
      .then((result) => setReviews(result.items))
      .catch(() => setReviews([]));
  }, []);

  useEffect(load, [load]);

  async function moreCourses() {
    setBusy('more');
    try {
      const result = await api<CoursePage>(coursesPath(coursePage + 1));
      setCourses((current) => [...(current ?? []), ...result.items]);
      setCourseTotal(result.totalItems);
      setCoursePage(result.page);
    } catch (caught) {
      setError(caught as ApiError);
    } finally {
      setBusy(null);
    }
  }

  /**
   * Approve a course, or send it back.
   *
   * Approving publishes it: there is no intermediate APPROVED state that
   * anything acts on, so a reviewer saying yes is the course going live.
   * Sending it back needs a reason — the author sees nothing else.
   */
  async function decideCourse(id: string, approve: boolean) {
    setError(null);
    setNotice(null);
    const reason = note[id]?.trim();

    if (!approve && !reason) {
      setError({
        code: 'VALIDATION_ERROR',
        message: 'Say why it is going back — the author only sees this note.',
        status: 400,
      });
      return;
    }

    setBusy(id);
    try {
      await api(`/admin/courses/${id}/${approve ? 'publish' : 'return'}`, {
        method: 'POST',
        body: approve ? undefined : JSON.stringify({ note: reason }),
      });
      setNotice(approve ? 'Course published. It is live in the catalog.' : 'Sent back to the author.');
      setNote((current) => ({ ...current, [id]: '' }));
      load();
    } catch (caught) {
      setError(caught as ApiError);
    } finally {
      setBusy(null);
    }
  }

  async function decide(id: string, approve: boolean) {
    setError(null);
    setNotice(null);
    const reason = note[id]?.trim();

    if (!approve && !reason) {
      setError({
        code: 'VALIDATION_ERROR',
        message: 'A reason is required when rejecting content.',
        status: 400,
      });
      return;
    }

    try {
      await api(`/content/${id}/${approve ? 'approve' : 'reject'}`, {
        method: 'POST',
        body: JSON.stringify({ note: reason }),
      });
      setNotice(approve ? 'Approved. Publish it to make it live.' : 'Sent back to the author.');
      load();
    } catch (caught) {
      setError(caught as ApiError);
    }
  }

  /**
   * Approve and publish everything waiting, in one go.
   *
   * Importing a document creates one content item per section, so a
   * twenty-section tutorial puts twenty items here at once. Each needs
   * approving and then publishing — forty clicks to clear one import, and
   * the course stays unpublishable until the last of them.
   *
   * Nothing is skipped: this walks the same two transitions the individual
   * buttons do, one item at a time, so the audit log reads exactly as it
   * would have. What it removes is the clicking, not the gate. An item that
   * refuses — a file still in quarantine, say — is counted and named rather
   * than stopping the rest.
   */
  async function approveAll() {
    if (!content || content.length === 0) return;
    setBusy('all');
    setError(null);
    setNotice(null);

    let done = 0;
    const failed: string[] = [];
    for (const item of content) {
      try {
        await api(`/content/${item.id}/approve`, { method: 'POST', body: JSON.stringify({}) });
        await api(`/content/${item.id}/publish`, { method: 'POST', body: JSON.stringify({}) });
        done += 1;
      } catch {
        failed.push(item.title);
      }
    }

    setNotice(
      `${done} item${done === 1 ? '' : 's'} approved and published.` +
        (failed.length > 0 ? ` ${failed.length} could not be: ${failed.join(', ')}.` : ''),
    );
    setBusy(null);
    load();
  }

  async function moderate(id: string, approve: boolean) {
    try {
      await api(`/admin/reviews/${id}/${approve ? 'approve' : 'reject'}`, { method: 'POST' });
      setNotice(approve ? 'Review published.' : 'Review rejected.');
      load();
    } catch (caught) {
      setError(caught as ApiError);
    }
  }

  return (
    <>
      <div className="page__head">
        <h1 className="page__title">Review queue</h1>
        <p className="page__lede">
          Nothing reaches learners without a decision here, and every decision is written to the
          audit log with its author.
        </p>
      </div>

      {error ? <Alert kind="error" title={error.message} items={error.details} /> : null}
      {notice ? <Alert kind="success" title={notice} /> : null}

      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card__head">
          <span className="card__title">
            Courses awaiting review {courses ? `(${courseTotal})` : ''}
          </span>
        </div>

        {!courses ? (
          <div className="spinner">Loading…</div>
        ) : courses.length === 0 ? (
          <div className="empty">Nothing waiting. Submitted courses will appear here.</div>
        ) : (
          courses.map((course) => (
            <div
              key={course.id}
              className="card__body"
              style={{ borderBottom: '1px solid var(--line)' }}
            >
              <div className="row" style={{ alignItems: 'center', marginBottom: 10 }}>
                <div style={{ flex: 1 }}>
                  <strong>{course.title}</strong>
                  <br />
                  <span className="muted" style={{ fontSize: 12.5 }}>
                    {course.category} · {course.moduleCount} module
                    {course.moduleCount === 1 ? '' : 's'} · {duration(course.durationMinutes)} ·{' '}
                    {course.isFree ? 'Free' : money(course.priceMinor, course.currency)}
                    {course.instructors.length > 0
                      ? ` · ${course.instructors.join(', ')}`
                      : ' · no instructor assigned'}
                  </span>
                </div>
                {/* Read it before deciding: approving a course unseen is no review. */}
                <Link className="btn btn--outline btn--sm" to={`/admin/courses/${course.id}`}>
                  Open builder
                </Link>
              </div>

              <div className="row">
                <input
                  className="field__input"
                  placeholder="Reason — required to send back, ignored on approval"
                  value={note[course.id] ?? ''}
                  onChange={(event) => setNote({ ...note, [course.id]: event.target.value })}
                />
                <button
                  className="btn btn--sm"
                  disabled={busy === course.id}
                  onClick={() => void decideCourse(course.id, true)}
                >
                  Approve &amp; publish
                </button>
                <button
                  className="btn btn--ghost btn--sm"
                  disabled={busy === course.id}
                  onClick={() => void decideCourse(course.id, false)}
                >
                  Send back
                </button>
              </div>
            </div>
          ))
        )}

        {courses && courses.length < courseTotal ? (
          <div className="listmore">
            <span>
              Showing {courses.length} of {courseTotal}
            </span>
            <button
              className="btn btn--ghost btn--sm"
              type="button"
              disabled={busy !== null}
              onClick={() => void moreCourses()}
            >
              {busy === 'more' ? 'Loading…' : 'Load more'}
            </button>
          </div>
        ) : null}
      </div>

      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card__head">
          <span className="card__title">
            Content awaiting review {content ? `(${content.length})` : ''}
          </span>
          {content && content.length > 1 ? (
            <button
              className="btn btn--ghost btn--sm"
              type="button"
              disabled={busy !== null}
              onClick={() => void approveAll()}
            >
              {busy === 'all' ? 'Working…' : `Approve and publish all ${content.length}`}
            </button>
          ) : null}
        </div>

        {!content ? (
          <div className="spinner">Loading…</div>
        ) : content.length === 0 ? (
          <div className="empty">Nothing waiting. Submitted content will appear here.</div>
        ) : (
          content.map((item) => (
            <div
              key={item.id}
              className="card__body"
              style={{ borderBottom: '1px solid var(--line)' }}
            >
              <div className="row" style={{ alignItems: 'center', marginBottom: 10 }}>
                <div style={{ flex: 1 }}>
                  <strong>{item.title}</strong>
                  <br />
                  <span className="muted" style={{ fontSize: 12.5 }}>
                    {titleCase(item.kind)} · version {item.versions}
                    {!item.servable ? ` · not servable (${item.scanStatus})` : ''}
                  </span>
                </div>
                {/* Read it before deciding: approving unseen content is no review. */}
                <button className="btn btn--outline btn--sm" onClick={() => setPreviewing(item)}>
                  Preview
                </button>
              </div>

              <div className="row">
                <input
                  className="field__input"
                  placeholder="Reason — required to reject, optional to approve"
                  value={note[item.id] ?? ''}
                  onChange={(event) => setNote({ ...note, [item.id]: event.target.value })}
                />
                <button className="btn btn--sm" onClick={() => decide(item.id, true)}>
                  Approve
                </button>
                <button className="btn btn--ghost btn--sm" onClick={() => decide(item.id, false)}>
                  Reject
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      <div className="card">
        <div className="card__head">
          <span className="card__title">
            Learner reviews awaiting moderation {reviews ? `(${reviews.length})` : ''}
          </span>
        </div>

        {!reviews ? (
          <div className="spinner">Loading…</div>
        ) : reviews.length === 0 ? (
          <div className="empty">No reviews waiting.</div>
        ) : (
          <table className="grid">
            <thead>
              <tr>
                <th>Course</th>
                <th>Rating</th>
                <th>Review</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {reviews.map((review) => (
                <tr key={review.id}>
                  <td>{review.course}</td>
                  <td>
                    {/* Always five, filled and empty, as on the course page. */}
                    <span
                      className="rating"
                      role="img"
                      aria-label={`${review.rating} out of 5 stars`}
                      style={{ gap: 0, whiteSpace: 'nowrap' }}
                    >
                      <span className="rating__star">{'★'.repeat(review.rating)}</span>
                      <span className="muted">{'★'.repeat(5 - review.rating)}</span>
                    </span>
                  </td>
                  <td>
                    {review.title ? <strong>{review.title}</strong> : null}
                    {review.body ? (
                      <p style={{ margin: 0, fontSize: 13, color: 'var(--ink-soft)' }}>
                        {review.body}
                      </p>
                    ) : null}
                  </td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button className="iconbtn" onClick={() => moderate(review.id, true)}>
                      Approve
                    </button>{' '}
                    <button
                      className="iconbtn iconbtn--danger"
                      onClick={() => moderate(review.id, false)}
                    >
                      Reject
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {previewing ? (
        <ContentPreviewModal item={previewing} onClose={() => setPreviewing(null)} />
      ) : null}
    </>
  );
}
